import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { createTeam, getTeams, type Team } from "../lib/api";
import { getTenantId, useTenant } from "../lib/tenant";

const POLL_MS = 60_000;
// <option> sentinel for the inline create flow — never a real teamId.
const NEW_TEAM = "__new-team__";

/** Dashboard-header tenant switcher (phase 6 §2/§5): Personal + the
 *  caller's teams, an inline "+ New team" create, and a settings link for
 *  the selected team. Selection persists in localStorage and every api.ts
 *  call rides it. Pre-phase-6 planes 404 GET /teams — the switcher hides. */
export function TenantSwitcher() {
  const { tenantId, setTenantId } = useTenant();
  const [teams, setTeams] = useState<Team[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    try {
      const ts = await getTeams();
      setTeams(ts);
      // Stale selection (left/deleted team) → back to Personal so every
      // tenant-scoped call resolves again instead of 403ing.
      const selected = getTenantId();
      if (selected && !ts.some((t) => t.teamId === selected)) {
        setTenantId(null);
      }
    } catch {
      setTeams(null); // plane without /teams yet — render nothing
    }
  }, [setTenantId]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  useEffect(() => {
    if (creating) nameRef.current?.focus();
  }, [creating]);

  if (teams === null) return null;

  const onSelect = (value: string) => {
    setErr(null);
    if (value === NEW_TEAM) {
      setCreating(true);
      return;
    }
    setCreating(false);
    setTenantId(value || null);
  };

  const create = async () => {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const { teamId } = await createTeam(name);
      setNewName("");
      setCreating(false);
      await refresh();
      setTenantId(teamId);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tenant-switcher">
      <select
        className="input tenant-select mono"
        aria-label="tenant"
        title="who this dashboard is operating as — Personal, or a shared team"
        value={creating ? NEW_TEAM : (tenantId ?? "")}
        onChange={(e) => onSelect(e.target.value)}
      >
        <option value="">Personal</option>
        {teams.map((t) => (
          <option key={t.teamId} value={t.teamId}>
            {t.name}
            {t.status === "invited" ? " (invited — open team settings)" : ""}
          </option>
        ))}
        <option value={NEW_TEAM}>+ New team</option>
      </select>
      {!creating && tenantId && (
        <Link
          className="row-session-link mono"
          to={`/teams/${tenantId}`}
          title="members, settings, and enroll instructions"
        >
          team settings →
        </Link>
      )}
      {creating && (
        <>
          <input
            ref={nameRef}
            className="input tenant-new-name"
            placeholder="team name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") create();
              if (e.key === "Escape") setCreating(false);
            }}
          />
          <button
            className="btn btn-sm"
            disabled={busy || !newName.trim()}
            onClick={create}
          >
            {busy ? "Creating…" : "Create"}
          </button>
          <button
            className="btn-ghost btn-sm"
            onClick={() => {
              setCreating(false);
              setNewName("");
              setErr(null);
            }}
          >
            Cancel
          </button>
        </>
      )}
      {err && <span className="hint-error">{err}</span>}
    </div>
  );
}
